import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SessionStorageService {
  /**
   * Get an item from session storage.
   * Automatically parses JSON if possible.
   */
  getItem<T>(key: string): T | null {
    const item = sessionStorage.getItem(key);

    if (item === null || item === 'undefined' || item === 'null') {
      return null;
    }

    // Attempt to parse JSON, return string if fails
    try {
      return JSON.parse(item) as T;
    } catch {
      return item as unknown as T;
    }
  }

  /**
   * Set an item in session storage.
   * Automatically stringifies objects.
   */
  setItem(key: string, value: any): void {
    if (value === null || value === undefined) {
      // sessionStorage returns 'null' or 'undefined'
      // as strings, so we remove the item instead
      sessionStorage.removeItem(key);
      return;
    }

    if (typeof value === 'object') {
      sessionStorage.setItem(key, JSON.stringify(value));
    } else {
      sessionStorage.setItem(key, String(value));
    }
  }

  removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Retrieves a Date object from session storage.
   * @why This method exists separate from `getItem` to handle a specific legacy issue where
   * the string "Invalid date" was saved to storage (likely from a failed Moment.js format).
   * @when_to_delete You can delete this method and use standard `getItem<string>()` logic
   * once the upstream bug saving "Invalid date" is fixed and you are confident
   * no active user sessions contain this corrupted string.
   */
  getDate(key: string): Date | null {
    const value = this.getItem<string>(key);
    if (!value || value === 'Invalid date') {
      return null;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Returns the stored date or the provided default if missing/invalid.
   */
  getDateOrDefault(key: string, defaultDate: Date): Date {
    const stored = this.getDate(key);
    return stored !== null ? stored : defaultDate;
  }

  clearAll(): void {
    sessionStorage.clear();
  }
}
